/**
 * Studio Series Query Hooks
 *
 * `useStudioSeriesDetail` accepts a `refetchInterval` so consumers can poll
 * during `pending_review` (60s, per contract §11). Foundation does not pick
 * the interval — caller decides.
 */

import { useQuery } from '@tanstack/react-query';
import { studioSeriesClient } from '../client/studio-series.client';
import { queryKeys } from '../query-keys';
import type {
  StudioSeriesDetail,
  StudioSeriesListFilters,
  StudioSeriesListResponse,
} from '@/types/studio-series';

export function useStudioSeries(
  filters: StudioSeriesListFilters = {},
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    initialData?: StudioSeriesListResponse;
  }
) {
  return useQuery({
    queryKey: queryKeys.studio.series.list(filters as Record<string, unknown>),
    queryFn: () => studioSeriesClient.list(filters),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    ...(options?.refetchInterval !== undefined && { refetchInterval: options.refetchInterval }),
    ...(options?.initialData !== undefined && { initialData: options.initialData }),
  });
}

export function useStudioSeriesDetail(
  uuid: string | null | undefined,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    initialData?: StudioSeriesDetail;
  }
) {
  return useQuery({
    queryKey: queryKeys.studio.series.detail(uuid ?? ''),
    queryFn: () => studioSeriesClient.get(uuid!),
    enabled: !!uuid && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    ...(options?.refetchInterval !== undefined && { refetchInterval: options.refetchInterval }),
    ...(options?.initialData !== undefined && { initialData: options.initialData }),
  });
}
